import { useCallback, useContext, useEffect } from 'preact/hooks';
import { ToastServiceContext, ToastType } from '@uw/common-components';
import { ApplicationContext } from 'contexts/use-application-setup';
import { tokenStore } from 'global/token.store';
import { stateStore } from 'global/state.store';
import { MANAGE_STATE } from 'constants/constants';
import { generateRandomId } from '@uw/utils';
import { api } from '../api';
import { verifyAccountCode } from '../utils/jwt-utils';

export const OAUTH: OrgInfo['ssoProtocol'] = 'OAUTH';
export const SAML: OrgInfo['ssoProtocol'] = 'SAML';
export const NEW_WINDOW_PARAMS =
  'height=700,width=700,status=yes,toolbar=no,menubar=no,location=no';
export const SSO_PATH_ERROR = 'No ssoPath is provided in the orgInfo';
export const POPUP_ERROR = 'Popup blocked! Please allow popups for this site.';
export const SSO_PROTOCOL_ERROR = 'Wrong ssoProtocol is provided in the orgInfo';
export const SSO_ERROR_TOAST_TEXT = 'SSO authentication error';
export const LA_LOGIN_ERROR = "We couldn't log you in. Please try again";
export const ACCOUNT_CODE_MISMATCH_ERROR =
  'Account code mismatch. Please log in with the correct account.';

let laLoginState: string;

export const useSso = () => {
  const { orgInfo, thisSiteId, code, tunings } = useContext(ApplicationContext);
  const { showToast } = useContext(ToastServiceContext);

  const isLaLoginAllowed = tunings?.isLAUser ?? false;
  const isSsoAllowed = !!orgInfo?.allows?.includes('SSO_LOGIN') && !isLaLoginAllowed;
  const ssoProtocol = orgInfo.ssoProtocol;
  const ssoPath = orgInfo.ssoPath;

  const ssoLogin = () => {
    if (!ssoPath && !isLaLoginAllowed) {
      console.error(SSO_PATH_ERROR);
      return;
    }
    if (!(ssoProtocol === OAUTH || ssoProtocol === SAML) && !isLaLoginAllowed) {
      console.error(SSO_PROTOCOL_ERROR);
      return;
    }

    const newWindow = window.open(
      buildSsoUrl({ ssoPath, ssoProtocol, code, thisSiteId, isLaLoginAllowed }),
      '_blank',
      NEW_WINDOW_PARAMS,
    );
    if (!newWindow) {
      console.warn(POPUP_ERROR);
    }
  };

  const handleMessageEvent = useCallback((event: MessageEvent) => {
    if (event.data?.SOCIAL_LOGIN_SUCCESS) {
      const { authCode, state } = event.data.SOCIAL_LOGIN_SUCCESS;

      if (isLaLoginAllowed && laLoginState !== state) {
        console.warn('LA login state mismatch');
        showToast(ToastType.Error, LA_LOGIN_ERROR);
        return;
      }

      socialLoginSuccess(authCode);
    }
    if (event.data?.SOCIAL_LOGIN_FAILURE) {
      const { errorMessage, responseErrorType } = event.data.SOCIAL_LOGIN_FAILURE;

      console.warn(
        isLaLoginAllowed ? 'LA login error' : 'SSO authentication error',
        responseErrorType || errorMessage,
      );
      showToast(ToastType.Error, isLaLoginAllowed ? LA_LOGIN_ERROR : SSO_ERROR_TOAST_TEXT);
    }
  }, []);

  const socialLoginSuccess = (authCode: string) => {
    const tempToken = `AuthCode ${authCode}`;
    const options = {
      headers: {
        Authorization: tempToken,
        'X-Sso-Request-Origin': 'WIDGET_ORIGIN',
      },
    };

    api.get(`${__API_BASE__}sso/v0/user/login/oauth-exchange`, undefined, options).then(
      (responseWithToken) => {
        const accessToken = responseWithToken.payload.accessToken;
        const isAccountCodeMismatch = !verifyAccountCode(accessToken, code);

        if (isAccountCodeMismatch) {
          showToast(ToastType.Error, ACCOUNT_CODE_MISMATCH_ERROR);
          return;
        }

        tokenStore.setToken(accessToken);

        stateStore.state.next(MANAGE_STATE);
      },
      (err) => {
        showToast(ToastType.Error, isLaLoginAllowed ? LA_LOGIN_ERROR : err.message);
      },
    );
  };

  useEffect(() => {
    // Waiting for a message from the SSO popup
    window.addEventListener('message', handleMessageEvent);

    return () => window.removeEventListener('message', handleMessageEvent);
  }, []);

  return {
    isSsoAllowed,
    ssoLogin,
    isLaLoginAllowed,
  };
};

interface BuildSsoUrl {
  ssoProtocol: OrgInfo['ssoProtocol'];
  ssoPath: string;
  code: string;
  thisSiteId: string;
  isLaLoginAllowed: boolean;
}

declare const applicationConfig: { base: { LALoginLink: string } };

export const buildSsoUrl = (params: BuildSsoUrl): string => {
  // workaround to catch local development env, for now node env return incorrect value for dev/qa
  const isLocalhost =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  if (params.isLaLoginAllowed) {
    laLoginState = generateRandomId();
    /**
     * callerOrigin specifies where to send the auth code back to the SSO server after login.
     * It's skipped on localhost because the SSO server ignores it there (uses a dev fallback),
     * sending a localhost origin to a real SSO server would fail its validation.
     */
    const callerOriginParam = isLocalhost
      ? ''
      : `&callerOrigin=${encodeURIComponent(window.location.origin)}`;

    return `${applicationConfig.base.LALoginLink}&userwayAccountCode=${params.code}${callerOriginParam}&userwayState=${laLoginState}&userwayFailureRedirectUri=${encodeURIComponent(
      `${__API_BASE__}sso/oauth-failed?errorMessage=`,
    )}`;
  }

  const protocol = params.ssoProtocol.toLowerCase();
  const redirectUriBase = isLocalhost
    ? `${__BASE_API_SSO__}sso/oauth-logged-in?authCode=`
    : `${__BASE_API_SSO__}sso/oauth-logged-in?callerOrigin=${encodeURIComponent(window.location.origin)}&authCode=`;

  const ssoUrl = `${__BASE_API_SSO__}sso/v0/public/login/${protocol}-init/${
    params.ssoPath
  }?${protocol}2_request_origin=WIDGET_ORIGIN&${protocol}2_redirect_uri=${encodeURIComponent(
    redirectUriBase,
  )}&${protocol}2_failure_redirect_uri=${encodeURIComponent(
    `${__BASE_API_SSO__}sso/oauth-failed?errorMessage=`,
  )}&${protocol}2_target_acc_code=${params.code}&${protocol}2_target_site_id=${params.thisSiteId}`;

  return ssoUrl;
};
